import { observer } from 'mobx-react';
import { When } from 'react-if';
import { Trans } from 'react-i18next';
import { Popover, theme } from 'antd';
import { useTranslation } from 'react-i18next';

import { DeviceConnectivity } from '@/constants/enums';
import { DeviceType } from '@/constants/deviceTypeEnum';
import { useStores } from '@/hooks/use-stores';
import { useMeasurement } from '@/hooks/useMeasurement';
import { useValueByConnectivity } from '@/hooks/useValueByConnectivity';
import Super from '@/comp/Super';
import { AlertsWidget } from '@/comp/MapBuilder/components/AlertsWidget';
import { Ports } from '@/comp/MapBuilder/components/Ports';
import { getOnDeviceSensorsToDisplay, getWidgetFillColor, isSelectedNodeOnMap } from '@/comp/MapBuilder/utils/utils';
import { CustomThemeToken } from '@/theme';
import ProductionFloorWidget from '@/comp/common/icons/ProductionFloorWidget';

import { Container } from './ProductionFloorNodeWidget.styles';
import { getBorderInfo } from '../compressor/CompressorNodeWidget.utils';
import ProductionFloorMaintenanceWidget from '@/comp/common/icons/ProductionFloorMaintenanceWidget';

const { useToken } = theme;

export const ProductionFloorNodeWidget = observer(({ node, engine = null, width, height }) => {
  const { t } = useTranslation();
  const { tunit, tvalue } = useMeasurement();
  const { tcunit, tcvalue } = useValueByConnectivity();
  const { name } = node.device;
  const { mapBuilderStore } = useStores();
  const { token } = useToken() as unknown as { token: CustomThemeToken };

  const { deviceId, inMaintenance } = node.getEntity();
  const renderedOnMap = !!engine;
  const isSelected = isSelectedNodeOnMap(deviceId, mapBuilderStore.selectedDevice?.deviceId, renderedOnMap);
  const widgetFillColor = getWidgetFillColor(isSelected, token, renderedOnMap);
  const borderInfo = getBorderInfo(isSelected, inMaintenance, false, false, token);

  const getPressureSensorValue = device => {
    return tcvalue(tvalue(device?.pressureBar, 'Bar'), device?.connectivity?.toLowerCase());
  };

  const getFlowSensorValue = device => {
    return tcvalue(tvalue(device?.flowM3Min, 'm^3/min'), device?.connectivity?.toLowerCase());
  };

  const [pressureSensors, flowSensors] = getOnDeviceSensorsToDisplay(node?.onDevices, [
    DeviceType.PressureSensor,
    DeviceType.FlowSensor,
  ]);
  const firstPressureSensor = pressureSensors?.[0];
  const firstFlowSensor = flowSensors?.[0];

  return (
    <Container width={width} height={height} $mapView={engine ? -1 : 1} $textColor={borderInfo?.textColor}>
      {borderInfo ? (
        <ProductionFloorMaintenanceWidget
          width={width}
          height={height}
          fillColor={borderInfo.fillColor}
          borderColor={borderInfo.borderColor}
          className="background"
        />
      ) : (
        <ProductionFloorWidget width={width} height={height} color={widgetFillColor} className="background" />
      )}

      <When condition={!!engine}>
        {borderInfo && <div className="title">{t(borderInfo.headerText)}</div>}
        <header>
          <Popover content={name}>
            <div className="name">{name}</div>
          </Popover>
        </header>

        <section>
          <div className="main-container">
            <div className="measurement-container">
              {firstPressureSensor && (
                <div className="measurement-item">
                  <div className="text-container">
                    <span className="value">{getPressureSensorValue(firstPressureSensor)}</span>
                    {firstPressureSensor?.connectivity?.toLowerCase() !== DeviceConnectivity.NoState && (
                      <span className="label">
                        {tcunit(
                          tunit('Bar'),
                          getPressureSensorValue(firstPressureSensor),
                          firstPressureSensor?.connectivity,
                        )}
                      </span>
                    )}
                  </div>
                </div>
              )}
              {firstFlowSensor && (
                <div className="measurement-item">
                  <div className="text-container">
                    <span className="value">{getFlowSensorValue(firstFlowSensor)}</span>
                    {firstFlowSensor.connectivity?.toLowerCase() !== DeviceConnectivity.NoState && (
                      <span className="label">
                        <Trans
                          i18nKey={tcunit(
                            tunit('m3/min'),
                            getFlowSensorValue(firstFlowSensor),
                            firstFlowSensor.connectivity,
                          )}
                        >
                          <Super />
                        </Trans>
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
            <AlertsWidget alerts={node.alerts} />
          </div>
        </section>

        <Ports node={node} engine={engine} width={width} height={height} />
      </When>
    </Container>
  );
});
